import { doc } from 'firebase/firestore'
import type { DocumentReference } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { updateDoc, setDoc } from '../lib/firestore'
import { useFirestoreDoc } from './useFirestore'
import type { NotificationPreferences, NotificationType } from '../types'
import { DEFAULT_NOTIFICATION_PREFERENCES } from '@shared/notifications'
import type { EmailCategory } from '@shared/email'
import type { User } from 'firebase/auth'

export function useNotificationPreferences(user: User | null) {
  const ref = user
    ? (doc(db, 'users', user.uid, 'private', 'notifications') as DocumentReference<NotificationPreferences>)
    : null
  const { data, loading } = useFirestoreDoc<NotificationPreferences>(ref)

  const prefs: NotificationPreferences = {
    ...DEFAULT_NOTIFICATION_PREFERENCES,
    ...data,
    categories: {
      ...DEFAULT_NOTIFICATION_PREFERENCES.categories,
      ...data?.categories,
    },
  }

  async function updatePreferences(patch: Partial<NotificationPreferences>) {
    if (!ref) return
    if (!data) {
      await setDoc(ref, { ...DEFAULT_NOTIFICATION_PREFERENCES, ...patch })
    } else {
      await updateDoc(ref, patch)
    }
  }

  async function toggleCategory(category: NotificationType, enabled: boolean) {
    if (!ref) return
    if (!data) {
      await setDoc(ref, {
        ...DEFAULT_NOTIFICATION_PREFERENCES,
        categories: { ...DEFAULT_NOTIFICATION_PREFERENCES.categories, [category]: enabled },
      })
    } else {
      await updateDoc(ref, { [`categories.${category}`]: enabled })
    }
  }

  async function setEmailCategory(category: EmailCategory, enabled: boolean) {
    if (!ref) return
    if (!data) {
      await setDoc(ref, { ...DEFAULT_NOTIFICATION_PREFERENCES, emailCategories: { [category]: enabled } })
    } else {
      await updateDoc(ref, { [`emailCategories.${category}`]: enabled })
    }
  }

  return { prefs, loading, updatePreferences, toggleCategory, setEmailCategory }
}
